import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../../config";

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("access_token")}` };
}

export default function ParadeTransactModal({ row, mode, onClose }) {
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const load = row?.load_snapshot || {};
  const truckerName = row?.trucker_name || "";
  const truckerPhone = row?.trucker_phone || load?.trucker_phone || "";
  const isBook = mode === "book";

  useEffect(() => {
    if (!row) return;
    const seed = load.payment ?? load.offer_rate ?? "";
    setAmount(seed === null || seed === undefined ? "" : String(seed));
    setResult(null);
    setError("");
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.booking_request_id, mode]);

  if (!row) return null;

  async function handleSubmit(e) {
    e?.preventDefault?.();
    setError("");
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) {
      setError("Enter the dollar amount.");
      return;
    }
    setSubmitting(true);
    try {
      const url = isBook
        ? `${API_URL}/parade/dispatch/book`
        : `${API_URL}/parade/dispatch/quote`;
      const body = { load };
      if (truckerPhone) body.trucker_phone = truckerPhone;
      if (truckerName) body.trucker_name = truckerName;
      if (isBook) {
        body.offer_rate = parsed;
      } else {
        body.quote_amount = parsed;
      }
      const { data } = await axios.post(url, body, { headers: authHeaders() });
      setResult(data);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          (isBook ? "Booking failed" : "Quote failed")
      );
    } finally {
      setSubmitting(false);
    }
  }

  const route =
    [load.origin_city, load.origin_state].filter(Boolean).join(", ") +
    " → " +
    ([load.destination_city, load.destination_state].filter(Boolean).join(", ") || "Any");

  return (
    <div
      onClick={submitting ? undefined : onClose}
      className="fixed inset-0 z-[1000] flex items-center justify-center px-5 bg-slate-900/60"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl p-7 w-full max-w-md shadow-2xl text-slate-900"
      >
        {!result ? (
          <>
            <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-2">
              {isBook ? "Book this Parade load" : "Quote this Parade load"}
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              For{" "}
              <strong className="font-semibold text-slate-900">
                {truckerName || "—"}
              </strong>{" "}
              · {route}
              {load.company_name ? ` · ${load.company_name}` : ""}
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {isBook ? "Book at rate (USD)" : "Quote amount (USD)"}
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-slate-500 text-sm">
                    $
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="1"
                    step="1"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0"
                    autoFocus
                    className="w-full pl-7 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  />
                </div>
                {!isBook && load.payment != null && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Posted rate: ${Number(load.payment).toLocaleString()}
                  </p>
                )}
                {row.negotiation_amount_cents != null && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    Trucker counter-offer: $
                    {(row.negotiation_amount_cents / 100).toLocaleString()}
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
                  {typeof error === "string" ? error : JSON.stringify(error)}
                </div>
              )}

              <div className="flex justify-end gap-2.5 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="!shadow-none !bg-white hover:!bg-slate-50 !text-slate-700 !border !border-slate-300 !rounded-lg !px-3.5 !py-2 !text-sm !font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || (!truckerPhone && !truckerName)}
                  className="!shadow-none !bg-indigo-600 hover:!bg-indigo-700 disabled:!opacity-60 !text-white !border-0 !rounded-lg !px-3.5 !py-2 !text-sm !font-semibold"
                >
                  {submitting
                    ? isBook
                      ? "Booking…"
                      : "Quoting…"
                    : isBook
                    ? "Book"
                    : "Submit quote"}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-2">
              {isBook ? "Booking submitted" : "Quote submitted"}
            </h2>
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3.5 py-3 text-sm mb-3">
              {result.message || "Sent to Parade."}
              {result.id ? (
                <span className="block text-emerald-700 mt-0.5">
                  {isBook ? "Reservation" : "Quote"} ID: {result.id}
                </span>
              ) : null}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="!shadow-none !bg-indigo-600 hover:!bg-indigo-700 !text-white !border-0 !rounded-lg !px-3.5 !py-2 !text-sm !font-semibold"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
